import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, ArrowRight, Eye } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { UpvMark, DoItSignature } from "@/components/brand/Brand";
import { LangSwitcher } from "@/components/i18n/LangSwitcher";
import { StatusBadge } from "@/components/company/StatusBadge";
import { CompanyLogo } from "@/components/company/CompanyLogo";
import { ProfilePreview } from "@/components/company/ProfileView";
import { appCompany } from "@/lib/demo/companies";
import { KpiNumber } from "@/components/common/KpiNumber";
import { useDemo } from "@/lib/demo/store";
import { useI18n } from "@/lib/i18n/i18n";
import { STATUSES } from "@/lib/demo/applications";

const FILTERS = ["all", ...Object.keys(STATUSES)];

const DetailSheet = ({ app, onClose }) => {
  const { state, updateApplication } = useDemo();
  const { t, lang } = useI18n();
  const [preview, setPreview] = useState(false);
  const act = (patch, msg) => { updateApplication(app.id, patch); toast.success(t(msg)); };
  const approve = () => {
    const n = state.companyApplications.filter((a) => a.status === "approved").length + 1;
    act({ status: "approved", stand: `D-${String(n).padStart(2, "0")}` }, "Empresa aprobada");
  };
  return (
    <Sheet open={!!app} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-lg" data-testid="backoffice-sheet">
        {app && (
          <div>
            <div className="bg-slate-950 px-8 pb-8 pt-10 text-white">
              <StatusBadge status={app.status} testId="backoffice-sheet-status" />
              <SheetHeader className="mt-4 space-y-1 text-left">
                <SheetTitle className="font-display text-2xl font-extrabold tracking-tight text-white" data-testid="backoffice-sheet-name">{app.name}</SheetTitle>
                <SheetDescription className="text-white/70">{app.cif} · {t(app.sector)} · {app.ref}</SheetDescription>
              </SheetHeader>
            </div>
            <div className="space-y-6 px-8 py-8 text-sm">
              <div className="relative overflow-hidden rounded-2xl border border-slate-200">
                <img src={appCompany(app).image} alt={app.name} className="h-36 w-full object-cover" data-testid="backoffice-sheet-cover" />
                <div className="absolute bottom-3 left-3 rounded-xl bg-white px-3 py-2 shadow-md"><CompanyLogo id={app.id} logo={appCompany(app).logo} size="text-lg" /></div>
              </div>
              <p className="whitespace-pre-line leading-relaxed text-slate-700">{app.description}</p>
              <div className="flex flex-wrap gap-2">{app.titulaciones.map((x) => <span key={x} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold">{t(x)}</span>)}</div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Oportunidades de empleo")}</p>
                <ul className="mt-2 space-y-1">{app.offers.map((o, i) => <li key={i}>{t(o.title)} · <span className="text-slate-500">{[t(o.type), o.area && t(o.area), o.location].filter(Boolean).join(" · ")}</span></li>)}</ul>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Usuarios")}</p>
                <ul className="mt-2 space-y-1">{app.users.filter((u) => u.name).map((u, i) => <li key={i} data-testid={`backoffice-user-${i}`}><span className="font-semibold">{u.name}</span> <span className="text-slate-500">{u.email} · {t(u.role === "admin" ? "Administrador" : "Reclutador")}</span></li>)}</ul>
              </div>
              <div className="rounded-2xl bg-slate-50 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Transferencia bancaria")}</p>
                <p className="mt-2">{t("Concepto")}: <span className="font-mono font-semibold">{app.ref}</span></p>
                <p className="mt-1 flex items-center gap-2"><Paperclip className="h-4 w-4 text-slate-400" /> {app.receipt || t("Sin justificante")}</p>
                <p className="mt-1 text-xs text-slate-400">{new Date(app.createdAt).toLocaleDateString(lang)}</p>
              </div>
              <div className="grid gap-3">
                <button onClick={() => setPreview(true)} className="flex h-12 items-center justify-center gap-2 rounded-xl border-2 border-slate-200 font-bold transition-colors hover:border-slate-900" data-testid="backoffice-preview-button"><Eye className="h-4 w-4" /> {t("Vista previa del perfil")}</button>
                {["pending_payment", "payment_review"].includes(app.status) && <button onClick={() => act({ status: "paid" }, "Pago marcado como recibido")} className="h-12 rounded-xl bg-slate-950 font-bold text-white transition-opacity hover:opacity-90" data-testid="backoffice-mark-paid-button">{t("Marcar pago recibido")}</button>}
                {app.status === "paid" && <button onClick={approve} className="h-12 rounded-xl bg-emerald-700 font-bold text-white transition-colors hover:bg-emerald-800" data-testid="backoffice-approve-button">{t("Aprobar empresa")}</button>}
                {!["approved", "rejected"].includes(app.status) && <button onClick={() => act({ status: "rejected" }, "Solicitud rechazada")} className="h-12 rounded-xl border-2 border-slate-900 font-bold transition-colors hover:bg-slate-900 hover:text-white" data-testid="backoffice-reject-button">{t("Rechazar")}</button>}
                <Link to={`/empresas/inscripcion/${app.id}`} className="flex items-center justify-center gap-2 text-sm font-semibold text-upv" data-testid="backoffice-view-application-link">{t("Ver solicitud")} <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
            <ProfilePreview open={preview} onOpenChange={setPreview} data={app} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function Backoffice() {
  const { state } = useDemo();
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const apps = useMemo(() => [...state.companyApplications].sort((a, b) => b.createdAt - a.createdAt), [state]);
  const count = (s) => apps.filter((a) => a.status === s).length;
  const rows = filter === "all" ? apps : apps.filter((a) => a.status === filter);
  const kpis = [["Solicitudes", apps.length, "total", true], ["Pendiente de pago", count("pending_payment"), "pending"], ["Pago en verificación", count("payment_review"), "review"], ["Aprobada", count("approved"), "approved"]];

  return (
    <div className="min-h-screen bg-[#F6F6F4] pb-28" data-testid="backoffice-page">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 lg:px-10">
          <UpvMark />
          <div className="flex items-center gap-4"><LangSwitcher testPrefix="backoffice-lang" /><DoItSignature className="hidden sm:block" /></div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] space-y-6 px-5 py-10 lg:px-10">
        <div className="flex flex-col gap-6 pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">Backoffice UPV</p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid="backoffice-title">{t("Inscripción de empresas")}</h1>
          </div>
          <div className="no-scrollbar flex w-full gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 sm:w-fit" data-testid="backoffice-filter">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-bold tracking-wider transition-colors ${filter === f ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`backoffice-filter-${f}`}>
                {t(f === "all" ? "Todas" : STATUSES[f].label)}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {kpis.map(([l, v, id, hl]) => (
            <div key={id} className={`rounded-[24px] border p-6 ${hl ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white"}`}>
              <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${hl ? "text-white/50" : "text-slate-400"}`}>{t(l)}</p>
              <KpiNumber value={v} testId={`backoffice-kpi-${id}`} className="mt-3 font-display text-5xl font-bold tracking-tight" />
            </div>
          ))}
        </div>
        <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Empresa", "CIF / NIF", "Sector", "Fecha", "Referencia", "Estado"].map((h) => <TableHead key={h} className="h-12 text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(h)}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id} onClick={() => setOpenId(a.id)} className="cursor-pointer" data-testid={`backoffice-row-${a.id}`}>
                    <TableCell className="py-4 font-semibold">{a.name}</TableCell>
                    <TableCell className="font-mono text-sm">{a.cif}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{t(a.sector)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{new Date(a.createdAt).toLocaleDateString(lang)}</TableCell>
                    <TableCell className="font-mono text-sm font-bold">{a.ref}</TableCell>
                    <TableCell><StatusBadge status={a.status} testId={`backoffice-row-status-${a.id}`} /></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500" data-testid="backoffice-empty">{t("No hay solicitudes en este estado.")}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
      <DetailSheet app={apps.find((a) => a.id === openId) || null} onClose={() => setOpenId(null)} />
    </div>
  );
}
